/* import __COLOCATED_TEMPLATE__ from './adc-inputs.hbs'; */
import Component from '@ember/component';
import { service } from '@ember/service';
import { computed, action } from '@ember/object';

import Constants from '../../utils/constants';

export default class AdcInputs extends Component {
    tagName = '';

    @service intl;
    @service('data') model;
    @service errorHandling;
    @service urlData;
    @service selectionData;
    @service sigmaDeltaData;
    @service componentManager;
    @service hashService;
    @service hashDataService;
    @service driverSupplyService;
    @service driverService;

    // qaAttrValue = `${Constants.QA_ATTR_PREFIX}_adc_bx`;

    panelHeadingBodyArgs = {};
    bodyArgsByVariant = {};

    init() {
        super.init();

        this.panelHeadingBodyArgs = {
            sarAdcParametricSearchUrl: `${this.urlData.analogBaseParametricSearchUrl}/${Constants.URL.SAR_ADCS}`,
            precisionAdcParametricSearchUrl: `${this.urlData.analogBaseParametricSearchUrl}/${Constants.URL.PRECISION_ADCS}`,
        };

        this.bodyArgsByVariant[Constants.VARIANT.ADC.HIZ] = {
            analogPinCompatibleArticleLink: this.intl.t('urls.articles.pinCompatible'),
        };

        this.set('componentManager.adcInputs', this);
    }

    @action showDeviceTable() {
        if (this.openDeviceTable) {
            this.openDeviceTable('adc');
        }
    }

    @action openCardTooltip() {
        if (this.openHelp) {
            this.openHelp(`info_modal.adc_input.title`, `info_modal.adc_input.body`, { ...this.panelHeadingBodyArgs });
        }
    }

    @action onSamplingRateChange() {
        this.hashDataService.setUrlParameter('model.samplingRate');
        if (this.updateAppData) this.updateAppData();
    }

    @action onVrefSelectChange() {
        this.set('model.vref', this.model.vrefSelect.id);
        this.onVrefChange();
    }

    @action onVrefChange() {
        this.model.handleVcm();
        this.driverSupplyService.calculateVSupply();
        this.hashDataService.setUrlParameter('model.vref');
        this.hashDataService.setUrlParameter('model.vSupplyPos');
        this.hashDataService.setUrlParameter('model.vSupplyNeg');
        if (this.updateAppData) this.updateAppData();
    }

    @action onAdcVariantChange() {
        this.model.switchVariant();
        this.set('model.zoomFitScaling', true);

        this.hashDataService.setUrlParameterWithSelectedPart('adc');
        this.hashDataService.setUrlParameter('model.showAdcVariant');
        this.hashDataService.setUrlParameterForObject('showAdcVariants');

        if (this.updateAppData) this.updateAppData();
    }

    @action onOdrChange() {
        this.model.overrideFsample();
        this.hashDataService.setUrlParameter('sigmaDeltaData.odrInput');
        this.hashDataService.setUrlParameter('model.samplingRate');

        if (this.updateAppData) this.updateAppData();
    }

    @action onMclkChange() {
        this.model.overrideFsample();
        this.hashDataService.setUrlParameter('sigmaDeltaData.mclk');
        this.hashDataService.setUrlParameter('model.samplingRate');

        if (this.updateAppData) this.updateAppData();
    }

    @action onMclkDivChange() {
        this.model.overrideFsample();
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'mclkDiv');
        this.hashDataService.setUrlParameter('model.samplingRate');

        if (this.updateAppData) this.updateAppData();
    }

    @action onPowerModeChange() {
        this.model.overrideRCValues();

        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'powerMode');
        this.hashDataService.setUrlParameter('model.rext');
        this.hashDataService.setUrlParameter('model.cext');

        if (this.updateAppData) this.updateAppData();
    }

    @action onFilterTypeChange() {
        this.sigmaDeltaData.setFilterTypeProperties();
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'filterType');
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'decimationRate');

        if (this.updateAppData) this.updateAppData();
    }

    @action onChannelAveragingChange() {
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'channelAveraging');
        if (this.updateAppData) this.updateAppData();
    }

    @action onDecimationRateChange() {
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'decimationRate');

        if (this.updateAppData) this.updateAppData();
    }

    @computed('selectionData.selectedAdc.vrefOptions') get vrefOptions() {
        return this.selectionData.selectedAdc.vrefOptions
            ? this.selectionData.selectedAdc.vrefOptions.map((option) => {
                  return { id: option, description: option };
              })
            : null;
    }

    /**
     * These should be the same for all variant codes
     * Variations go in bodyArgsByVariant
     */
    @computed('selectionData.selectedAdc.{partCore,datasheet}') get variantModalBodyArgs() {
        return {
            adc: this.selectionData.selectedAdc.partCore,
            datasheet: this.selectionData.selectedAdc.datasheet,
            lastUpdateDate: new Date(),
        };
    }

    /**
     * Can only be used in initialLoadWithHash
     */
    canCalculateProperty(propertyName) {
        let hashValues = this.hashService.parsedLocationHash;
        return !(hashValues[propertyName] && Number(hashValues[propertyName]) !== 0);
    }

    initialLoadWithHash() {
        if (this.canCalculateProperty('multiplexed')) {
            this.model.setMultiplexedMetadata();
        }

        if (this.canCalculateProperty('showAdcVariant')) this.model.updateShowVariant();
        this.model.switchVariant();
        this.sigmaDeltaData.refreshSigmaDeltaDefaults();
        this.model.resetVrefSelect();
        this.model.overrideFsample();

        let canCalculateSDRext = this.sigmaDeltaData.isSigmaDeltaAdc && this.canCalculateProperty('rext'),
            canCalculateSDCext = this.sigmaDeltaData.isSigmaDeltaAdc && this.canCalculateProperty('cext');

        this.model.overrideRCValues({
            resetRext: false,
            resetSDRext: canCalculateSDRext,
            resetSDCext: canCalculateSDCext,
        });

        this.model.handleVcm(this.canCalculateProperty('vcm'));

        this.hashDataService.setUrlParameter('model.showAdcVariant');
        this.hashDataService.setUrlParameterForObject('showAdcVariants');

        this.hashDataService.setUrlParameter('model.samplingRate');
        this.hashDataService.setUrlParameter('model.vref');
        this.hashDataService.setUrlParameterWithSelectedPart('adc');

        this.hashDataService.setUrlParameter('sigmaDeltaData.odrInput');
        this.hashDataService.setUrlParameter('sigmaDeltaData.mclk');
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'mclkDiv');
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'powerMode');
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'filterType');
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'channelAveraging');
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'decimationRate');
    }

    initialLoadWithoutHash() {
        // Make sure all the data is loaded
        this.model.setMultiplexedMetadata();
        this.model.updateShowVariant();
        this.model.switchVariant();
        this.sigmaDeltaData.refreshSigmaDeltaDefaults();
        this.model.resetVrefSelect();
        this.model.overrideFsample();
        this.model.overrideRCValues();
        this.model.handleVcm();

        this.hashDataService.setUrlParameter('model.showAdcVariant');
        this.hashDataService.setUrlParameterForObject('showAdcVariants');

        this.hashDataService.setUrlParameter('model.samplingRate');
        this.hashDataService.setUrlParameter('model.vref');
        this.hashDataService.setUrlParameterWithSelectedPart('adc');

        this.hashDataService.setUrlParameter('sigmaDeltaData.odrInput');
        this.hashDataService.setUrlParameter('sigmaDeltaData.mclk');
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'mclkDiv');
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'powerMode');
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'filterType');
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'channelAveraging');
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'decimationRate');
    }
}
